import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ClockCounterClockwise, FunnelSimple } from "@phosphor-icons/react";
import api from "../lib/api";
import { SectionHeader } from "../components/shared";
import { DataTable } from "../components/DataTable";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const ACTION_STYLE = {
  "PO Created": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "PO Edited": "bg-blue-50 text-blue-700 border-blue-200",
  "PO Submitted for Approval": "bg-violet-50 text-violet-700 border-violet-200",
  "PO Approved": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "PO Declined": "bg-red-50 text-red-700 border-red-200",
  "PO Received": "bg-teal-50 text-teal-700 border-teal-200",
  "Vendor Payment": "bg-amber-50 text-amber-700 border-amber-200",
  "Sales Order Created": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Sales Quote Created": "bg-sky-50 text-sky-700 border-sky-200",
  "Sales Invoice Created": "bg-indigo-50 text-indigo-700 border-indigo-200",
  "Customer Payment": "bg-amber-50 text-amber-700 border-amber-200",
  "User Invited": "bg-blue-50 text-blue-700 border-blue-200",
  "Role Changed": "bg-violet-50 text-violet-700 border-violet-200",
  "User Removed": "bg-red-50 text-red-700 border-red-200",
};

const ROLE_LABEL = { admin: "Administrator", accountant: "Accountant", sales: "Sales", purchasing: "Purchasing", viewer: "Viewer", "": "—" };

function fmt(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function AuditLog() {
  const [entity, setEntity] = useState("all");
  const [action, setAction] = useState("all");

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["audit-log", entity, action],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (entity !== "all") params.set("entity_type", entity);
      if (action !== "all") params.set("action", action);
      params.set("limit", "300");
      return (await api.get(`/audit-log?${params.toString()}`)).data;
    },
    refetchInterval: 30000,
  });

  const actionOptions = Array.from(new Set(rows.map((r) => r.action))).sort();

  return (
    <div className="animate-fade-up" data-testid="audit-log-page">
      <SectionHeader title="Audit Log" subtitle="Who did what, and when — across key actions">
        <div className="flex items-center gap-2 flex-wrap">
          <FunnelSimple size={16} className="text-muted-foreground" />
          <Select value={entity} onValueChange={setEntity}>
            <SelectTrigger className="rounded-sm h-9 w-44 text-xs" data-testid="audit-entity-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All entities</SelectItem>
              <SelectItem value="purchase_order">Purchase Orders</SelectItem>
              <SelectItem value="sales_order">Sales Documents</SelectItem>
              <SelectItem value="user">Users</SelectItem>
            </SelectContent>
          </Select>
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger className="rounded-sm h-9 w-52 text-xs" data-testid="audit-action-filter"><SelectValue placeholder="All actions" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All actions</SelectItem>
              {actionOptions.map((a) => <SelectItem key={a} value={a}>{a}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </SectionHeader>

      {isLoading ? (
        <div className="flex justify-center py-16"><div className="h-7 w-7 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
      ) : (
        <DataTable
          testid="audit-log-table"
          data={rows}
          rowKey={(r) => r.id}
          rowTestid="audit-row"
          empty="No audit activity recorded yet."
          columns={[
            { key: "at", header: "When", cellClassName: "text-xs text-muted-foreground font-mono whitespace-nowrap", cell: (r) => fmt(r.at) },
            { key: "user_name", header: "User", filter: "text", cellClassName: "text-sm", cell: (r) => (
              <div><div className="font-medium">{r.user_name || "—"}</div><div className="text-xs text-muted-foreground">{ROLE_LABEL[r.role] ?? r.role}</div></div>
            ) },
            { key: "action", header: "Action", filter: false, cell: (r) => (
              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold border ${ACTION_STYLE[r.action] || "bg-secondary text-foreground border-border"}`} data-testid="audit-action-badge">{r.action}</span>
            ) },
            { key: "doc_no", header: "Reference", filter: "text", cellClassName: "font-mono text-xs", cell: (r) => r.doc_no || "—" },
            { key: "detail", header: "Detail", cellClassName: "text-sm text-muted-foreground", cell: (r) => r.detail || "—" },
          ]}
        />
      )}
      <p className="mt-3 flex items-center gap-1.5 text-xs text-muted-foreground"><ClockCounterClockwise size={13} /> Showing the {rows.length} most recent entries. Rapid auto-save edits are grouped.</p>
    </div>
  );
}
